import React, { useEffect, useCallback, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "../../i18n";
import { lockBodyScroll, unlockBodyScroll } from "../../lib/bodyScrollLock";
import { isTopOverlay, registerOverlay, unregisterOverlay } from "../../lib/overlayStack";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export default function Modal({
  open,
  onClose,
  title,
  children,
  footer = /** @type {React.ReactNode} */ (null),
  size = "md",
  className = "",
  contentClassName = "",
  panelRef: externalPanelRef = /** @type {React.RefObject<HTMLDivElement | null> | undefined} */ (
    undefined
  ),
  initialFocusRef = /** @type {React.RefObject<HTMLElement | null> | undefined} */ (undefined),
  overlayPriority = 120,
  /// Standard "side" (Max, 19.09.2026): jedes Pop-up fliegt als Blatt von rechts über die volle
  /// Höhe ein, der Rest der Seite wird abgedunkelt. "center" nur noch ausdrücklich.
  /// "bottom": Sheet von unten für mobile Werkzeugmenüs im Daumenbereich.
  placement = "side",
}) {
  const internalPanelRef = useRef(null);
  const panelRef = externalPanelRef || internalPanelRef;
  const returnFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const overlayIdRef = useRef(null);
  const titleId = useId();
  const { t } = useTranslation();

  // Ein offenes Modal darf seinen Fokus-Lifecycle nicht neu starten, nur weil
  // der Parent (z. B. durch Polling) einen neuen Callback rendert. Der
  // Keydown-Handler bleibt stabil und liest trotzdem immer den aktuellen
  // onClose-Callback.
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape" && isTopOverlay(overlayIdRef.current)) {
      e.preventDefault();
      e.stopPropagation();
      onCloseRef.current?.();
      return;
    }

    // Focus trap
    if (e.key === "Tab" && panelRef.current && isTopOverlay(overlayIdRef.current)) {
      const focusable = panelRef.current.querySelectorAll(FOCUSABLE);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  useEffect(() => {
    if (open) {
      returnFocusRef.current = document.activeElement;
      overlayIdRef.current = registerOverlay(overlayPriority);
      document.addEventListener("keydown", handleKeyDown);
      lockBodyScroll();
      // Auto-focus first focusable element
      const focusFrame = requestAnimationFrame(() => {
        const focusable = panelRef.current?.querySelectorAll(FOCUSABLE);
        const preferred = initialFocusRef?.current;
        const target = preferred && !preferred.matches(":disabled") ? preferred : focusable?.[0];
        target?.focus({ preventScroll: true });
      });
      return () => {
        cancelAnimationFrame(focusFrame);
        document.removeEventListener("keydown", handleKeyDown);
        unregisterOverlay(overlayIdRef.current);
        overlayIdRef.current = null;
        unlockBodyScroll();
        returnFocusRef.current?.focus?.();
      };
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, handleKeyDown, overlayPriority]);

  if (!open) return null;

  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    "2xl": "max-w-6xl",
  };
  const side = placement === "side";
  const bottom = placement === "bottom";

  return createPortal(
    <div
      className={
        side
          ? "fixed inset-0 flex items-stretch justify-end"
          : bottom
            ? "fixed inset-0 flex items-end"
            : "fixed inset-0 flex items-center justify-center p-4"
      }
      style={{ zIndex: overlayPriority }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-label={title ? undefined : t("common.dialog.confirmTitle")}
    >
      {/* Backdrop */}
      <button
        type="button"
        tabIndex={-1}
        aria-label={t("common.close")}
        className={`absolute inset-0 ${side || bottom ? "bg-[rgba(6,8,10,.55)] backdrop-blur-[2px]" : "bg-[rgba(6,8,10,.6)]"}`}
        onClick={onClose}
      />

      {/* Panel — max-h + internes Scrolling: der Body-Scroll ist gesperrt,
          ohne Höhen-Deckel wäre zu hoher Dialog-Inhalt unerreichbar
          (03.07.2026, Attribut-Editor mit Beschreibungs-Vorlage). Header
          bleibt außerhalb der Scroll-Fläche stehen. */}
      <div
        ref={panelRef}
        className={
          side
            ? `relative z-10 flex h-full w-full ${sizeClasses[size]} flex-col overscroll-contain border-l border-outline-variant bg-surface-container p-5 shadow-[-24px_0_80px_rgba(0,0,0,0.55)] sheet-enter sm:p-6 ${className}`
            : bottom
              ? `relative z-10 flex max-h-[85dvh] w-full flex-col overscroll-contain rounded-t-2xl border-t border-outline-variant bg-surface-container px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-24px_80px_rgba(0,0,0,0.55)] sheet-rise ${className}`
              : `relative z-10 w-full ${sizeClasses[size]} max-h-[88vh] overscroll-contain flex flex-col rounded-xl bg-surface-container border border-outline-variant shadow-[0_40px_100px_rgba(0,0,0,0.6)] p-6 md:p-8 ${className}`
        }
      >
        {/* Header */}
        {(title || onClose) && (
          <div className={`flex items-center justify-between shrink-0 ${bottom ? "mb-3" : "mb-6"}`}>
            {title && (
              <h2 id={titleId} className="text-base font-semibold text-on-surface">
                {title}
              </h2>
            )}
            {onClose && (
              <button
                onClick={onClose}
                type="button"
                className="text-on-surface-variant hover:text-on-surface p-1 rounded-lg hover:bg-on-surface/[.06] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                aria-label={t("common.close")}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  close
                </span>
              </button>
            )}
          </div>
        )}

        {/* Content — scrollt bei Überlänge innerhalb des Panels */}
        <div
          className={`min-h-0 overflow-y-auto overscroll-contain -mr-3 pr-3 ${side || bottom ? "flex-1" : ""} ${contentClassName}`}
        >
          {children}
        </div>
        {footer && (
          <div
            className="shrink-0 border-t border-outline-variant pt-3 mt-3"
            data-slot="modal-footer"
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
